import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { edadEnFecha } from "@/lib/curp";
import { docenteLabel } from "@/lib/format";

const NIVEL_LABEL = { PREESCOLAR: "Preescolar", PRIMARIA: "Primaria", SECUNDARIA: "Secundaria" };

export default function DatosGenerales() {
  const { grupoId } = useParams();
  const { user, fechaCorteEdad } = useAuth();
  const [grupo, setGrupo] = useState(null);
  const [alumnos, setAlumnos] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [g, a] = await Promise.all([
          api.get(`/grupos/${grupoId}`),
          api.get("/alumnos", { params: { grupo_id: grupoId, include_bajas: false } }),
        ]);
        setGrupo(g.data);
        setAlumnos(a.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [grupoId]);

  const nivel = grupo?.plantel?.nivel || "";
  const cutoff = fechaCorteEdad?.(nivel) || "";

  const rows = useMemo(() => {
    const sorted = [...alumnos].sort((a, b) => {
      const A = `${a.apellido_paterno || ""} ${a.apellido_materno || ""} ${a.nombre || ""}`.trim().toUpperCase();
      const B = `${b.apellido_paterno || ""} ${b.apellido_materno || ""} ${b.nombre || ""}`.trim().toUpperCase();
      return A.localeCompare(B, "es");
    });
    return sorted.map((a, i) => ({
      n: i + 1,
      nombre: [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ").toUpperCase(),
      sexo: a.genero === "H" ? "H" : a.genero === "M" ? "M" : "",
      edad: edadEnFecha(a.fecha_nacimiento, cutoff),
      mama: (a.nombre_mama || "").toUpperCase(),
      tel_mama: a.telefono_mama || "",
      papa: (a.nombre_papa || "").toUpperCase(),
      tel_papa: a.telefono_papa || "",
      domicilio: (a.domicilio || "").toUpperCase(),
    }));
  }, [alumnos, cutoff]);

  const backTo = user?.role === "docente" ? "/docente/datos-generales" : "/admin/grupos";

  return (
    <AppLayout>
      {/* Tamaño oficio horizontal: 216mm x 330mm (Legal MX) landscape → 330x216 */}
      <style>{`
        @page { size: 330mm 216mm; margin: 8mm; }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          aside, header[data-app-header] { display: none !important; }
          .print-sheet { box-shadow: none !important; border: none !important; padding: 0 !important; margin: 0 !important; }
        }
      `}</style>

      <div className="max-w-none">
        <div className="no-print mb-4 flex items-center justify-between gap-2">
          <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-[#545454] hover:text-[#10312B]">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <div className="text-xs text-[#6B7280]">
            Formato tamaño oficio (216×330 mm) · horizontal · una sola hoja.
          </div>
          <Button
            className="bg-[#235B4E] hover:bg-[#10312B] text-white"
            data-testid="datos-generales-print"
            onClick={() => window.print()}
          >
            <Printer className="w-4 h-4 mr-1.5" /> Imprimir / PDF
          </Button>
        </div>

        {/* Hoja única oficio landscape ~314mm ancho útil */}
        <div className="print-sheet bg-white border border-[#E5E7EB] shadow-sm p-4 mx-auto"
             style={{ width: "min(100%, 314mm)" }}>
          <Membrete grupo={grupo} nivel={nivel} cutoff={cutoff} docente={grupo?.docente || (user?.role === "docente" ? user : null)} />
          <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-[#9F2241] font-semibold">
            Datos generales de los alumnos
          </div>
          <table className="w-full text-[10px] border-collapse mt-1" data-testid="datos-generales-tbl">
            <thead>
              <tr className="bg-[#10312B] text-white">
                <Th className="w-[3%] text-center">No.</Th>
                <Th className="w-[20%] text-left">NOMBRE<br/><span className="text-[8px] opacity-80 normal-case">APELLIDO PATERNO&nbsp;&nbsp;APELLIDO MATERNO&nbsp;&nbsp;NOMBRE(S)</span></Th>
                <Th className="w-[4%] text-center">SEXO</Th>
                <Th className="w-[4%] text-center">EDAD</Th>
                <Th className="w-[16%] text-left">NOMBRE DE LA MAMÁ (TUTOR)</Th>
                <Th className="w-[9%] text-center">TELÉFONO</Th>
                <Th className="w-[16%] text-left">NOMBRE DEL PAPÁ (TUTOR)</Th>
                <Th className="w-[9%] text-center">TELÉFONO</Th>
                <Th className="text-left">DOMICILIO</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="border border-[#D1D5DB] py-8 text-center text-[#9CA3AF]">
                    Sin alumnos registrados en este grupo.
                  </td>
                </tr>
              ) : rows.map((r) => (
                <tr key={r.n} className="odd:bg-white even:bg-[#F9FAFB]" data-testid={`dg-row-${r.n}`}>
                  <Td className="text-center font-mono">{r.n}</Td>
                  <Td>{r.nombre}</Td>
                  <Td className="text-center font-semibold">{r.sexo}</Td>
                  <Td className="text-center">{r.edad !== "" ? r.edad : ""}</Td>
                  <Td>{r.mama || <span className="text-[#D1D5DB]">&nbsp;</span>}</Td>
                  <Td className="text-center">{r.tel_mama}</Td>
                  <Td>{r.papa || <span className="text-[#D1D5DB]">&nbsp;</span>}</Td>
                  <Td className="text-center">{r.tel_papa}</Td>
                  <Td className="text-[9px]">{r.domicilio}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 text-[8px] text-[#9CA3AF] text-right">
            Generado por el Sistema Censo 911 · {new Date().toLocaleDateString("es-MX")}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Membrete({ grupo, nivel, cutoff, docente }) {
  return (
    <>
      <div className="border-b-2 border-[#10312B] pb-2 mb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png"
              alt="Censo 911"
              className="w-11 h-11 object-contain shrink-0"
              crossOrigin="anonymous"
            />
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#9F2241]">Sistema Censo 911 · Tamaulipas</div>
              <div className="text-[13px] font-bold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                DATOS GENERALES DE ALUMNOS
              </div>
              <div className="text-[10px] text-[#545454]">Formato interno · Datos por grupo</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Ciclo escolar</div>
            <div className="text-[11px] font-mono text-[#10312B]">{grupo?.ciclo || "—"}</div>
            <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF] mt-1">Fecha de corte edad</div>
            <div className="text-[10px] font-mono text-[#10312B]">{cutoff || "—"}</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-[11px]">
        <div className="col-span-2">
          <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Escuela: </span>
          <span className="font-semibold text-[#10312B]">{grupo?.plantel?.nombre || "—"}</span>
          <span className="ml-2 font-mono text-[10px] text-[#6B7280]">{grupo?.plantel?.cct || ""}</span>
        </div>
        <div>
          <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Nivel: </span>
          <span className="font-semibold text-[#10312B]">{NIVEL_LABEL[nivel] || nivel || "—"}</span>
        </div>
        <div className="text-right">
          <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Grado: </span>
          <span className="font-semibold text-[#10312B]">{grupo?.grado ?? "___"}°</span>
          <span className="ml-3 text-[9px] uppercase tracking-wider text-[#9CA3AF]">Grupo: </span>
          <span className="font-semibold text-[#10312B]">{grupo?.letra || "___"}</span>
        </div>
        <div className="col-span-4">
          <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Docente: </span>
          <span className="font-semibold text-[#10312B]">{docenteLabel(docente) || "—"}</span>
        </div>
      </div>
    </>
  );
}

function Th({ children, className = "" }) {
  return <th className={`border border-[#10312B] px-1.5 py-1 text-[9px] font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`border border-[#D1D5DB] px-1.5 py-1 ${className}`}>{children}</td>;
}
